import { getDataTable } from '../../features/visual-analytics/charts/chartDataBindings';
import { convertColumnDataToDisplayString } from '../../features/visual-analytics/helpers/misc';
import { getColumnInfo } from '../analyticsDataSlice';
import { TpCanonicalTypeInfo, TpDataContext } from '../interfaces';
import {
  TpActiveEnSelectionFilter,
  TpEnSelectionFilterDefinition,
  TpEnSelectionFilterStep,
  TpEnSelectionFilterStepNumericalRange,
  TpEnSelectionFilterTypes,
} from './interfaces';
import { getEnSelectionFilterStep, removeEnSelectionFilterStep } from './selectionEnFilters';
import { TpUserEnSelectionAction, TpUserEnSelectionActionNumericalRange } from './userSelectionActions';

function getNumericalRangeEnFilterStepKey(dataTableId: string, colId: string): string {
  return `numericalRange_${dataTableId}_${colId}`;
}

export function getNumericalRangeEnFilterStep(
  selectionFilter: TpActiveEnSelectionFilter,
  dataTableId: string,
  colId: string,
): TpEnSelectionFilterStepNumericalRange | null {
  const step = getEnSelectionFilterStep(selectionFilter, getNumericalRangeEnFilterStepKey(dataTableId, colId));
  if (!step) return null;
  return step as TpEnSelectionFilterStepNumericalRange;
}

function handleUserAction(
  state: any,
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  userSelectionAction: TpUserEnSelectionAction,
) {
  const userSelectionActionNumRange = userSelectionAction as TpUserEnSelectionActionNumericalRange;
  const key = getNumericalRangeEnFilterStepKey(
    userSelectionActionNumRange.dataTableId,
    userSelectionActionNumRange.colId,
  );
  const step = getEnSelectionFilterStep(canonicalTypeInfo.selectionFilter, key);

  if (isNaN(userSelectionActionNumRange.rangeMin) && isNaN(userSelectionActionNumRange.rangeMax)) {
    // this is actually an empty filter
    if (step) {
      // there is an existing filter, which we remove as the new one is empty
      removeEnSelectionFilterStep(canonicalTypeInfo.selectionFilter, key);
    }
  } else {
    // Non-empty filter
    let stepNumRange: TpEnSelectionFilterStepNumericalRange;
    if (!step) {
      // not yet existing - we create it
      stepNumRange = {
        type: TpEnSelectionFilterTypes.NUMERICALRANGE,
        key,
        dataTableId: userSelectionActionNumRange.dataTableId,
        colId: userSelectionActionNumRange.colId,
        rangeMin: userSelectionActionNumRange.rangeMin,
        rangeMax: userSelectionActionNumRange.rangeMax,
      };
      canonicalTypeInfo.selectionFilter.steps.push(stepNumRange);
    } else {
      // already existing - we update it
      stepNumRange = step as TpEnSelectionFilterStepNumericalRange;
      stepNumRange.rangeMin = userSelectionActionNumRange.rangeMin;
      stepNumRange.rangeMax = userSelectionActionNumRange.rangeMax;
    }
  }
}

function performStep(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  step: TpEnSelectionFilterStep,
): any[] {
  const stepNumRange = step as TpEnSelectionFilterStepNumericalRange;
  const dataTable = getDataTable(dataContext, stepNumRange.dataTableId);
  const itemIdList = dataTable.columnData[dataTable.colItemId];
  const valList = dataTable.columnData[stepNumRange.colId];
  const selectedItemIds = [];

  for (let itemIdx = 0; itemIdx < itemIdList.length; itemIdx++) {
    const val = valList[itemIdx];
    if (
      !isNaN(val) &&
      (val >= stepNumRange.rangeMin || isNaN(stepNumRange.rangeMin)) &&
      (val <= stepNumRange.rangeMax || isNaN(stepNumRange.rangeMax))
    )
      selectedItemIds.push(itemIdList[itemIdx]);
  }
  return selectedItemIds;
}

function renderContent(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  step: TpEnSelectionFilterStep,
  forceString?: boolean,
) {
  const stepNumRange = step as TpEnSelectionFilterStepNumericalRange;
  const dataTable = getDataTable(dataContext, stepNumRange.dataTableId);
  const colInfo = getColumnInfo(dataTable, stepNumRange.colId);
  if (!isNaN(stepNumRange.rangeMin) && !isNaN(stepNumRange.rangeMax)) {
    if (forceString)
      return `${colInfo.name} between [${convertColumnDataToDisplayString(
        colInfo,
        stepNumRange.rangeMin,
      )}, ${convertColumnDataToDisplayString(colInfo, stepNumRange.rangeMax)}]`;
    return (
      <span>
        <b>{colInfo.name}</b> between [{convertColumnDataToDisplayString(colInfo, stepNumRange.rangeMin)},
        {convertColumnDataToDisplayString(colInfo, stepNumRange.rangeMax)}]
      </span>
    );
  }
  if (!isNaN(stepNumRange.rangeMax)) {
    if (forceString) return `${colInfo.name}<=${convertColumnDataToDisplayString(colInfo, stepNumRange.rangeMax)}`;
    return (
      <span>
        <b>{colInfo.name}</b> &#8804; {convertColumnDataToDisplayString(colInfo, stepNumRange.rangeMax)}
      </span>
    );
  }
  if (!isNaN(stepNumRange.rangeMin)) {
    if (forceString) return `${colInfo.name}>=${convertColumnDataToDisplayString(colInfo, stepNumRange.rangeMin)}`;
    return (
      <span>
        <b>{colInfo.name}</b> &#8805; {convertColumnDataToDisplayString(colInfo, stepNumRange.rangeMin)}
      </span>
    );
  }
  return null;
}

export const defEnSelectionFilterNumericalRange: TpEnSelectionFilterDefinition = {
  handleUserAction,
  performStep,
  renderContent,
};
